'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { LayoutDashboard, CalendarCheck2, ListChecks, LineChart, ClipboardList, GraduationCap, BookMarked, Compass, ChevronsLeft, ChevronsRight, LogOut, X, Crown, Route, Receipt, Activity, Inbox, Wallet, UserX, Mail, Video, UserCheck, Star, HelpCircle as HelpIcon, FileSignature, PlayCircle, MessageSquare, Presentation, TrendingUp, Users, Kanban, CalendarClock, ClipboardCheck, Radio, BarChart3, PoundSterling, Megaphone, Settings } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useRole, portalIdentities, signOutAndClear, PortalRole } from '@/lib/roleSession';
import { createClient } from '@/lib/supabase/client';
import YourPlanCard from '@/components/YourPlanCard';
import { useUnreadMessages } from '@/lib/messaging/useUnreadMessages';
import { useStaffAccess } from '@/components/RouteGuard';
import { useSessionCache } from '@/lib/session/sessionCache';

// Local session snapshot/cache helpers (module-level, shared across Sidebar instances)
interface LocalSessionSnapshot {
  hasVideos: boolean | null;
  lastBadgeFetch: number;
  badges: { enquiries: number; pending: number; cancellations: number };
}

const localSessionSnapshot: LocalSessionSnapshot = {
  hasVideos: null,
  lastBadgeFetch: 0,
  badges: { enquiries: 0, pending: 0, cancellations: 0 },
};

function getSessionSnapshot(): LocalSessionSnapshot {
  return localSessionSnapshot;
}

function patchSessionCache(patch: Partial<LocalSessionSnapshot>) {
  Object.assign(localSessionSnapshot, patch);
}



interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

interface NavItem {
  key: string;
  label: string;
  href: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  badge?: number;
  badgeTone?: 'gold' | 'danger';
}

const workspaceNav: NavItem[] = [
  { key: 'nav-dashboard', label: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { key: 'nav-journey', label: 'My Business Journey', href: '/implementation-tracker', icon: Route },
  { key: 'nav-sessions', label: 'My Mentorship Sessions', href: '/mentorship-sessions', icon: CalendarCheck2 },
  { key: 'nav-actions', label: 'My Accountability', href: '/action-tracker', icon: ListChecks },
  { key: 'nav-progress', label: 'Progress Tracking', href: '/progress-tracking', icon: LineChart },
  { key: 'nav-my-courses', label: 'My Courses', href: '/courses/my', icon: BookMarked },
  { key: 'nav-courses', label: 'Course Catalogue', href: '/courses', icon: GraduationCap },
  { key: 'nav-next-step', label: "What's Your Next Step?", href: '/next-step', icon: Compass },
  { key: 'nav-my-payments', label: 'My payments', href: '/my-payments', icon: Wallet },
  { key: 'nav-my-agreements', label: 'My agreements', href: '/agreements', icon: FileSignature },
  { key: 'nav-my-videos', label: 'My videos', href: '/my-videos', icon: PlayCircle },
  { key: 'nav-messages', label: 'Messages', href: '/messages', icon: MessageSquare },
];

const mentorNav: NavItem[] = [
  { key: 'nav-journey', label: 'Member Journeys', href: '/implementation-tracker', icon: Route },
  { key: 'nav-sessions', label: 'Mentorship Sessions', href: '/mentorship-sessions', icon: CalendarCheck2 },
  { key: 'nav-actions', label: 'Verify Actions', href: '/action-tracker', icon: ListChecks },
  { key: 'nav-progress', label: 'Member Progress', href: '/admin-console/member-progress', icon: LineChart },
  { key: 'nav-enquiries', label: 'Enquiries', href: '/admin-console/enquiries', icon: Inbox },
  { key: 'nav-logins-activity', label: 'Logins & activity', href: '/admin-console/logins-activity', icon: Activity },
  { key: 'nav-messages', label: 'Messages', href: '/admin/messages', icon: MessageSquare },
];

const adminNav: NavItem[] = [
  { key: 'nav-admin', label: 'Superuser Console', href: '/admin-console', icon: Crown },
  { key: 'nav-messages', label: 'Messages', href: '/admin/messages', icon: MessageSquare },
  { key: 'nav-pending-signups', label: 'Not signed up yet', href: '/admin-console/pending-signups', icon: UserCheck },
  { key: 'nav-enquiries', label: 'Enquiries', href: '/admin-console/enquiries', icon: Inbox },
  { key: 'nav-emails', label: 'Emails', href: '/admin-console/emails', icon: Mail },
  { key: 'nav-webinar', label: 'Webinar', href: '/admin-console/webinar', icon: Video },
  { key: 'nav-webinar-studio', label: 'Webinar Studio', href: '/admin-console/webinar-studio', icon: Presentation },
  { key: 'nav-videos-admin', label: 'Videos', href: '/admin-console/videos', icon: PlayCircle },
  { key: 'nav-reviews', label: 'Reviews', href: '/admin-console/reviews', icon: Star },
  { key: 'nav-agreements-admin', label: 'Agreements', href: '/admin-console/agreements', icon: FileSignature },
  { key: 'nav-logins-activity', label: 'Logins & activity', href: '/admin-console/logins-activity', icon: Activity },
  { key: 'nav-payments-log', label: 'Payments log', href: '/admin-console/payments-log', icon: Receipt },
  { key: 'nav-cancellations', label: 'Cancellations', href: '/admin-console/cancellations', icon: UserX },
  { key: 'nav-journey', label: 'Member Journeys', href: '/implementation-tracker', icon: Route },
  { key: 'nav-sessions', label: 'Mentorship Sessions', href: '/mentorship-sessions', icon: CalendarCheck2 },
  { key: 'nav-actions', label: 'Accountability Tracker', href: '/action-tracker', icon: ListChecks },
  { key: 'nav-progress', label: 'Member Progress', href: '/admin-console/member-progress', icon: LineChart },
];

const accountNav: NavItem[] = [
  { key: 'nav-onboarding', label: 'Baseline Assessment', href: '/member-onboarding', icon: ClipboardList },
  { key: 'nav-help', label: 'Help Centre', href: '/help', icon: HelpIcon },
];

const salesNav: (NavItem & { superuserOnly?: boolean })[] = [
  { key: 'nav-sales-dashboard', label: 'Sales Dashboard', href: '/admin-console/sales', icon: TrendingUp },
  { key: 'nav-sales-leads', label: 'All Leads', href: '/admin-console/sales/leads', icon: Users },
  { key: 'nav-sales-pipeline', label: 'Sales Pipeline', href: '/admin-console/sales/pipeline', icon: Kanban },
  { key: 'nav-sales-consultations', label: 'Consultations', href: '/admin-console/sales/consultations', icon: CalendarClock },
  { key: 'nav-sales-tasks', label: 'Follow-up Tasks', href: '/admin-console/sales/tasks', icon: ClipboardCheck },
  { key: 'nav-sales-webinar', label: 'Webinar Leads', href: '/admin-console/sales/webinar', icon: Radio },
  { key: 'nav-sales-analytics', label: 'Conversion Analytics', href: '/admin-console/sales/analytics', icon: BarChart3 },
  { key: 'nav-sales-revenue', label: 'Revenue & Membership', href: '/admin-console/sales/revenue', icon: PoundSterling, superuserOnly: true },
  { key: 'nav-sales-sources', label: 'Marketing Sources', href: '/admin-console/sales/sources', icon: Megaphone },
  { key: 'nav-sales-settings', label: 'CRM Settings', href: '/admin-console/sales/settings', icon: Settings, superuserOnly: true },
];

const navByRole: Record<PortalRole, NavItem[]> = {
  member: workspaceNav,
  mentor: mentorNav,
  admin: adminNav,
};

function NavLink({ item, collapsed, active, onNavigate }: { item: NavItem; collapsed: boolean; active: boolean; onNavigate: () => void }) {
  const Icon = item.icon;
  return (
    <Link
      href={item.href}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      style={{ touchAction: 'manipulation' }}
      className={`group relative flex min-h-[44px] items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
        active
          ? 'bg-accent/20 text-accent' :'text-primary-foreground/75 hover:bg-primary-foreground/10 hover:text-primary-foreground'
      } ${collapsed ? 'justify-center' : ''}`}
    >
      {active && <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-accent" />}
      <Icon size={19} className="flex-shrink-0" />
      {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
      {!collapsed && item.badge ? (
        <span
          className={`tabular rounded-full px-2 py-0.5 text-[11px] font-bold ${
            item.badgeTone === 'danger' ? 'bg-destructive text-destructive-foreground' : 'bg-accent text-accent-foreground'
          }`}
        >
          {item.badge}
        </span>
      ) : null}
      {collapsed && item.badge ? (
        <span className="absolute right-2 top-1.5 h-2 w-2 rounded-full bg-accent" />
      ) : null}
      {collapsed && (
        <span className="pointer-events-none absolute left-full z-50 ml-3 whitespace-nowrap rounded-md bg-foreground px-2.5 py-1.5 text-xs font-medium text-background opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100">
          {item.label}
        </span>
      )}
    </Link>
  );
}

export default function Sidebar({ collapsed, onToggleCollapse, mobileOpen, onCloseMobile }: SidebarProps) {
  const pathname = usePathname();
  const storedRole = useRole();
  const staffAccess = useStaffAccess();
  // Staff links are only ever shown once the database confirms the user is staff.
  const role: PortalRole = staffAccess === true ? storedRole : 'member';
  const identity = portalIdentities[role];
  const baseNav = navByRole[role];
  const [newEnquiries, setNewEnquiries] = useState(0);
  const [receivedCancellations, setReceivedCancellations] = useState(0);
  const [pendingSignups, setPendingSignups] = useState(0);
  const [hasVideos, setHasVideos] = useState(false);
  const cached = useSessionCache();
  const crmStaff = cached.isCrmStaff;
  const crmSuper = cached.isCrmSuperuser;
  const unreadMessages = useUnreadMessages(true);
  const cachedBadges = cached.badges;
  let lastBadgeFetch = cached.lastBadgeFetch;

  // Always close the mobile menu whenever the route changes.
  useEffect(() => {
    onCloseMobile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Lock body scroll only while the mobile menu is open; always release on close/unmount.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  // An assistant (CRM staff who is not a mentor/admin) only ever sees the Sales section.
  const salesOnly = crmStaff && staffAccess !== true;
  const visibleSales = salesNav.filter((i) => !i.superuserOnly || crmSuper);

  useEffect(() => {
    if (role !== 'member') return;
    let cancelled = false;
    const known = getSessionSnapshot().hasVideos;
    if (known !== null) {
      setHasVideos(known);
      return;
    }
    const loadVideos = async () => {
      try {
        const supabase = createClient();
        const { data } = await supabase.rpc('my_member_resources');
        const has = Array.isArray(data) && data.length > 0;
        patchSessionCache({ hasVideos: has });
        if (!cancelled) setHasVideos(has);
      } catch {
        if (!cancelled) setHasVideos(false);
      }
    };
    void loadVideos();
    return () => {
      cancelled = true;
    };
  }, [role]);

  // Staff badge counts: load once, then refresh every 60s while the tab is visible.
  // Not tied to the route, so opening/closing the menu or navigating never refetches them.
  useEffect(() => {
    if (role !== 'admin' && role !== 'mentor') return;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const loadCounts = async () => {
      patchSessionCache({ lastBadgeFetch: Date.now() });
      const badges = getSessionSnapshot().badges;
      const supabase = createClient();
      try {
        const { count } = await supabase
          .from('membership_interest')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'new');
        if (!cancelled) {
          badges.enquiries = count ?? 0;
          setNewEnquiries(badges.enquiries);
        }
      } catch {
        // badge is optional
      }
      if (role !== 'admin') return;
      try {
        const { data } = await supabase.rpc('admin_pending_signups');
        if (!cancelled && Array.isArray(data)) {
          badges.pending = data.length;
          setPendingSignups(badges.pending);
        }
      } catch {
        // badge is optional
      }
      try {
        const { count } = await supabase
          .from('membership_cancellation_requests')
          .select('id', { count: 'exact', head: true })
          .eq('status', 'received');
        if (!cancelled) {
          badges.cancellations = count ?? 0;
          setReceivedCancellations(badges.cancellations);
        }
      } catch {
        // badge is optional
      }
    };

    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const start = () => {
      if (timer || document.visibilityState !== 'visible') return;
      timer = setInterval(() => void loadCounts(), 60000);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        if (Date.now() - getSessionSnapshot().lastBadgeFetch > 60000) void loadCounts();
        start();
      } else {
        stop();
      }
    };

    const snapBadges = getSessionSnapshot().badges;
    setNewEnquiries(snapBadges.enquiries);
    setPendingSignups(snapBadges.pending);
    setReceivedCancellations(snapBadges.cancellations);
    if (Date.now() - getSessionSnapshot().lastBadgeFetch > 60000) void loadCounts();
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [role]);

  const mainNav = baseNav.filter((item) => item.key !== 'nav-my-videos' || hasVideos).map((item) => {
    if (item.key === 'nav-enquiries') return { ...item, badge: newEnquiries > 0 ? newEnquiries : undefined };
    if (item.key === 'nav-cancellations') return { ...item, badge: receivedCancellations > 0 ? receivedCancellations : undefined };
    if (item.key === 'nav-pending-signups') return { ...item, badge: pendingSignups > 0 ? pendingSignups : undefined };
    if (item.key === 'nav-messages') return { ...item, badge: unreadMessages > 0 ? unreadMessages : undefined };
    return item;
  });

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    if (href === '/admin-console') return pathname === '/admin-console';
    if (href === '/admin-console/webinar') return pathname === href || pathname.startsWith(`${href}/`);
    if (href === '/courses') return pathname === '/courses' || (pathname.startsWith('/courses/') && !pathname.startsWith('/courses/my'));
    return pathname.startsWith(href);
  };

  return (
    <>
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={onCloseMobile}
          style={{ touchAction: 'manipulation' }}
          className="fixed inset-0 z-40 bg-foreground/50 lg:hidden"
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex flex-col bg-sidebar text-primary-foreground transition-transform duration-200 ease-out lg:transition-[width] lg:duration-200 ${
          collapsed ? 'lg:w-[72px]' : 'lg:w-[260px]'
        } w-[270px] ${mobileOpen ? 'translate-x-0 visible' : '-translate-x-full invisible pointer-events-none'} lg:visible lg:pointer-events-auto lg:translate-x-0`}
      >
        <div className={`flex h-16 items-center border-b border-primary-foreground/10 px-4 ${collapsed ? 'lg:justify-center' : 'justify-between'}`}>
          <div className="flex items-center gap-2.5">
            <AppLogo size={36} />
            {!collapsed && (
              <div className="leading-tight">
                <span className="block text-sm font-bold tracking-tight">Care Business School</span>
                <span className="block text-[11px] font-medium text-accent">{identity.roleLabel}</span>
              </div>
            )}
          </div>
          <button type="button" onClick={onCloseMobile} style={{ touchAction: 'manipulation' }} className="flex h-11 w-11 items-center justify-center rounded-md text-primary-foreground/70 hover:bg-primary-foreground/10 lg:hidden" aria-label="Close menu">
            <X size={18} />
          </button>
        </div>

        <nav className="scroll-thin flex-1 space-y-6 overflow-y-auto px-3 py-5">
          {!salesOnly && (
            <div>
              {!collapsed && <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.12em] text-primary-foreground/45">Workspace</p>}
              <div className="space-y-1">
                {mainNav.map((item) => (
                  <NavLink key={item.key} item={item} collapsed={collapsed} active={isActive(item.href)} onNavigate={onCloseMobile} />
                ))}
              </div>
            </div>
          )}
          {crmStaff && (
            <div>
              {!collapsed && <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.12em] text-primary-foreground/45">Sales &amp; Lead Generation</p>}
              <div className="space-y-1">
                {visibleSales.map((item) => (
                  <NavLink
                    key={item.key}
                    item={item}
                    collapsed={collapsed}
                    active={item.href === '/admin-console/sales' ? pathname === item.href : pathname.startsWith(item.href)}
                    onNavigate={onCloseMobile}
                  />
                ))}
              </div>
            </div>
          )}
          {!salesOnly && (
            <div>
              {!collapsed && <p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.12em] text-primary-foreground/45">Account</p>}
              <div className="space-y-1">
                {(role === 'member' ? accountNav : accountNav.filter((i) => i.key === 'nav-help')).map((item) => (
                  <NavLink key={item.key} item={item} collapsed={collapsed} active={isActive(item.href)} onNavigate={onCloseMobile} />
                ))}
              </div>
            </div>
          )}

          {!collapsed && role === 'member' && <YourPlanCard />}
        </nav>

        <div className="border-t border-primary-foreground/10 p-3">
          <Link
            href={identity.loginPath}
            onClick={() => {
              onCloseMobile();
              signOutAndClear();
            }}
            style={{ touchAction: 'manipulation' }}
            className={`group relative flex min-h-[44px] items-center gap-3 rounded-lg p-2 transition-colors duration-150 hover:bg-primary-foreground/10 ${collapsed ? 'lg:justify-center' : ''}`}
          >
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-accent-foreground">{identity.initials}</span>
            {!collapsed && (
              <span className="min-w-0 flex-1 leading-tight">
                <span className="block truncate text-sm font-semibold">{identity.name}</span>
                <span className="block truncate text-xs text-primary-foreground/60">{identity.subtitle}</span>
              </span>
            )}
            {!collapsed && <LogOut size={16} className="text-primary-foreground/60" />}
            {collapsed && (
              <span className="pointer-events-none absolute left-full z-50 ml-3 whitespace-nowrap rounded-md bg-foreground px-2.5 py-1.5 text-xs font-medium text-background opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100">
                Sign out
              </span>
            )}
          </Link>
          <div className={`mt-2 hidden items-center gap-1 lg:flex ${collapsed ? 'justify-center' : 'justify-between'}`}>
            {!collapsed && (
              <Link
                href="/help"
                onClick={onCloseMobile}
                className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs transition-colors duration-150 hover:bg-primary-foreground/10 hover:text-primary-foreground ${
                  pathname.startsWith('/help') ? 'text-accent' : 'text-primary-foreground/55'
                }`}
              >
                <HelpIcon size={14} /> Help centre
              </Link>
            )}
            <button
              type="button"
              onClick={onToggleCollapse}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="rounded-md p-2 text-primary-foreground/65 transition-colors duration-150 hover:bg-primary-foreground/10 hover:text-primary-foreground"
            >
              {collapsed ? <ChevronsRight size={17} /> : <ChevronsLeft size={17} />}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}